import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { MentionsInput, Mention } from 'react-mentions';
import Avatar from '@strava/ui/Avatar';

import I18n from 'utils/I18n';
import createNetworkingClient from 'utils/networking-client';

import Button from '@strava/ui/Button';
import defaultStyle from './defaultStyle';
import fetchAthletes from './cache';
import sortAndFilter from './utils';

import styles from './mentionable_comment.scss';
import { viewingAthleteProps } from '../../../props/feed-prop-types';
import { useFeedEntityContext } from '../../../state/FeedEntityContext';
import {
  ADD_COMMENT_FAILED,
  ADD_COMMENT_START,
  ADD_COMMENT_COMPLETE
} from '../../../state/feed-entity-actions';

const MentionableCommentForm = ({
  viewingAthlete,
  entity,
  entityId,
  placeHolder,
  buttonText,
  suggestionLimit,
  autoFocus,
  parent
}) => {
  const [mentionableAthletes, setMentionableAthletes] = useState([]);
  const [comment, setComment] = useState('');

  const placeHolderProp =
    placeHolder || I18n.t('templates.feed.comment_box.add_a_comment');
  const buttonTextProp =
    buttonText || I18n.t('templates.feed.comment_box.post');

  const { avatarUrl, name, id: viewingAthleteId, memberType } = viewingAthlete;

  useEffect(() => {
    fetchAthletes({ viewingAthleteId }).then((mentionableFollowers) => {
      setMentionableAthletes(mentionableFollowers);
    });
  }, [entity, entityId, viewingAthleteId]);

  const { entityState, dispatch } = useFeedEntityContext();

  // group activities use the same endpoint as activities
  const entityEndpoint =
    entity === 'group_activity' ? 'activity' : entity.toLowerCase();

  // Parses comment tokens into a string that can be shown to the user
  // example input:
  //     [{
  //         "type": "mention_token",
  //         "text": "Athlete Name",
  //         "path": "/athletes/10",
  //         "athlete_id": 1
  //     },
  //     {
  //         "type": "raw_token",
  //         "text": " text"
  //     }]
  //
  // output: <a href=/athletes/10>Athlete Name</a><span> text</span>
  const parseComment = (data) => {
    if (typeof data === 'string') {
      return data;
    }
    const formattedTokens = data.map((token) => {
      if (token.type === 'mention_token') {
        return `<a href=${token.path}>${token.text}</a>`;
      }
      return `<span>${token.text}</span>`;
    });
    return formattedTokens.join('');
  };

  const handleSubmitCommentComplete = (data) => {
    setComment('');
    const commentEntry = { ...data };
    commentEntry.comment = parseComment(data.comment);
    dispatch({
      type: ADD_COMMENT_COMPLETE,
      payload: { comment: commentEntry, entityId }
    });
  };

  const handleSubmitCommentFail = () => {
    dispatch({ type: ADD_COMMENT_FAILED });
  };

  const submitComment = (e) => {
    entityState.trackAnalytics({
      action: 'click',
      element: `post_comment_btn-on-${parent}`
    });
    e.preventDefault();
    dispatch({ type: ADD_COMMENT_START });
    createNetworkingClient()
      .post(`/feed/${entityEndpoint}/${entityId}/comment`, {
        comment
      })
      .then((response) => {
        if (response && response.status === 200) {
          handleSubmitCommentComplete(response.data);
        } else {
          handleSubmitCommentFail();
        }
      })
      .catch((error) => {
        handleSubmitCommentFail(error);
      });
  };

  const handleChange = (_, value) => {
    setComment(value);
  };

  const nameFromId = (id) => {
    return mentionableAthletes.find((a) => a.id === parseInt(id, 10)).display;
  };

  const filteredMentionableAthletes = (query) => {
    const results = mentionableAthletes.filter(
      (a) => a.display.toLowerCase().indexOf(query.toLowerCase()) >= 0
    );
    return results.slice(0, suggestionLimit);
  };

  const fetchEntityAthletes = (e) => {
    if (e.key === '@') {
      fetchAthletes({
        entity,
        entityId,
        viewingAthleteId
      }).then((mentionableEntityOwners) => {
        const filtered = sortAndFilter(
          mentionableAthletes.concat(mentionableEntityOwners),
          viewingAthleteId
        );
        setMentionableAthletes(filtered);
      });
    }
  };

  const renderSuggestion = (entry) => {
    const avatarStyle = { marginRight: '8px' };
    return (
      <div className={styles.suggestion} data-testid="suggestions">
        <Avatar
          name={entry.display}
          src={entry.profile}
          size="small"
          type="athlete"
          style={avatarStyle}
          badge={entry.member_type}
          onClick={() => {
            entityState.trackAnalytics({
              action: 'click',
              element: 'profile_picture',
              properties: { element_source: 'mentionable-athlete-suggestions' }
            });
          }}
        />
        <div className={styles.metaContainer}>
          <div className={styles.athleteName}>{entry.display}</div>
          {entry.location && entry.location.length > 0 && (
            <div className={`text-caption2 ${styles.location}`}>
              {entry.location}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className={styles.mentionableCommentWrapper}
      data-testid="comments-form"
    >
      <div className={styles.avatar}>
        <Avatar
          src={avatarUrl}
          type="athlete"
          name={name}
          href={`/athletes/${viewingAthleteId}`}
          badge={memberType}
          size="xsmall"
          onClick={() => {
            entityState.trackAnalytics({
              action: 'click',
              element: 'profile_picture',
              properties: {
                element_source: `comments-form-viewing-athlete-on-${parent}`
              }
            });
          }}
        />
      </div>
      <MentionsInput
        allowSpaceInQuery={true}
        autoFocus={autoFocus}
        style={defaultStyle}
        classNames={{
          mentions: styles.body,
          mentions__highlighter: styles.highlighter,
          mentions__suggestions: styles.suggestions,
          mentions__suggestions__list: styles.suggestionList,
          mentions__input: styles.textAreaInput
        }}
        value={comment}
        placeholder={placeHolderProp}
        onChange={handleChange}
        onKeyDown={fetchEntityAthletes}
      >
        <Mention
          appendSpaceOnAdd={true}
          data={filteredMentionableAthletes}
          displayTransform={nameFromId}
          markup="[strava://athletes/__id__]"
          renderSuggestion={renderSuggestion}
          className={styles.mentionedAthlete}
        />
      </MentionsInput>
      <div className={styles.postCommentBtn}>
        <Button
          type="button"
          data-testid="post-comment-btn"
          variant="text"
          disabled={entityState.isSubmittingComment || comment.length === 0}
          onClick={submitComment}
        >
          {buttonTextProp}
        </Button>
      </div>
    </div>
  );
};

MentionableCommentForm.defaultProps = {
  placeHolder: null,
  buttonText: null,
  suggestionLimit: 5,
  autoFocus: false,
  parent: 'feed_card'
};

MentionableCommentForm.propTypes = {
  entity: PropTypes.string.isRequired,
  entityId: PropTypes.string.isRequired,
  placeHolder: PropTypes.string,
  buttonText: PropTypes.string,
  suggestionLimit: PropTypes.number,
  autoFocus: PropTypes.bool,
  viewingAthlete: viewingAthleteProps.isRequired,
  parent: PropTypes.string
};

export default MentionableCommentForm;
